import * as React from 'react';
import { DocumentFormData } from '../types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Upload, Bot, FileText, Eye, Save } from 'lucide-react';
import { DocumentForm } from './DocumentForm';
import { DocumentFileManager } from './DocumentFileManager';
import { toast } from 'sonner';

interface DocumentCreationWizardProps {
  onSubmit: (data: DocumentFormData, fileInfo: UploadedFile) => void;
  onCancel: () => void;
  onFileUpload: (file: File) => Promise<any>;
  isLoading?: boolean;
}

interface UploadedFile {
  id: string;
  filename: string;
  storage_path: string;
  thumbnail_path: string | null;
  file_type: string;
  file_size: number;
  uploaded_at: string;
  ocr_text?: string;
  ocr_quality?: string;
}

interface ExtractedMetadata {
  title?: string;
  description?: string;
  document_type?: string;
  original_date?: string;
  original_date_precision?: string;
  location?: string;
  creator?: string;
  source?: string;
  archive_source_location?: string;
  historical_context?: string;
  publication_name?: string;
  page_number?: string;
  section?: string;
  tags?: string[];
  categories?: string[];
  extracted_entities?: {
    people?: string[];
    places?: string[];
    organizations?: string[];
    dates?: string[];
    events?: string[];
  };
}

type WizardStep = 'upload' | 'processing' | 'form' | 'review' | 'complete';

export function DocumentCreationWizard({ 
  onSubmit, 
  onCancel, 
  onFileUpload, 
  isLoading = false 
}: DocumentCreationWizardProps) {
  const [currentStep, setCurrentStep] = React.useState<WizardStep>('upload');
  const [uploadedFile, setUploadedFile] = React.useState<UploadedFile | null>(null);
  const [extractedMetadata, setExtractedMetadata] = React.useState<ExtractedMetadata | null>(null);
  const [formData, setFormData] = React.useState<DocumentFormData | null>(null);
  const [processingProgress, setProcessingProgress] = React.useState(0);
  const [isProcessing, setIsProcessing] = React.useState(false);

  const steps = [
    { id: 'upload', label: 'Upload File', icon: Upload },
    { id: 'processing', label: 'AI Processing', icon: Bot },
    { id: 'form', label: 'Review & Edit', icon: FileText },
    { id: 'review', label: 'Final Review', icon: Eye },
    { id: 'complete', label: 'Complete', icon: Save },
  ];

  const getCurrentStepIndex = () => steps.findIndex(step => step.id === currentStep);
  const getProgressPercentage = () => ((getCurrentStepIndex() + 1) / steps.length) * 100;

  const handleFileUpload = async (file: File) => {
    try {
      setIsProcessing(true);
      setCurrentStep('processing');
      setProcessingProgress(20);

      // Upload file
      const uploadResult = await onFileUpload(file);
      setUploadedFile(uploadResult);
      setProcessingProgress(60);

      // Simulate processing delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProcessingProgress(80);

      // Extract metadata from the upload result if available
      if (uploadResult.ocr_text || uploadResult.extracted_metadata) {
        const metadata: ExtractedMetadata = {
          title: extractTitle(uploadResult.ocr_text),
          description: extractDescription(uploadResult.ocr_text),
          document_type: uploadResult.extracted_metadata?.document_type || 'document',
          original_date: extractDate(uploadResult.ocr_text),
          original_date_precision: 'year',
          location: uploadResult.extracted_metadata?.location,
          creator: uploadResult.extracted_metadata?.creator,
          source: uploadResult.extracted_metadata?.source || 'Ontario Beach Trading Company Collection',
          historical_context: uploadResult.extracted_metadata?.historical_context,
          publication_name: uploadResult.extracted_metadata?.publication_name,
          page_number: uploadResult.extracted_metadata?.page_number,
          section: uploadResult.extracted_metadata?.section,
          tags: uploadResult.extracted_metadata?.tags || [],
          categories: uploadResult.extracted_metadata?.categories || [],
          extracted_entities: uploadResult.extracted_metadata?.extracted_entities
        };
        
        setExtractedMetadata(metadata);
      } else {
        // Create basic metadata from OCR text
        setExtractedMetadata({
          title: extractTitle(uploadResult.ocr_text),
          description: 'Historical document from the Ontario Beach Trading Company collection.',
          document_type: 'document',
          source: 'Ontario Beach Trading Company Collection',
          tags: [],
          categories: []
        });
      }

      setProcessingProgress(100);
      toast.success('File processed successfully!');
      
      // Move to form step after a brief delay
      setTimeout(() => {
        setCurrentStep('form');
        setIsProcessing(false);
      }, 500);

    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to process file');
      setCurrentStep('upload');
      setIsProcessing(false);
    }
  };

  const extractTitle = (ocrText?: string): string => {
    if (!ocrText) return '';
    const lines = ocrText.split('\n').filter(line => line.trim());
    return lines[0]?.substring(0, 100) || '';
  };

  const extractDescription = (ocrText?: string): string => {
    if (!ocrText) return '';
    const lines = ocrText.split('\n').filter(line => line.trim());
    const meaningfulLines = lines.slice(1, 4).join(' ');
    return meaningfulLines.substring(0, 200) || '';
  };

  const extractDate = (ocrText?: string): string => {
    if (!ocrText) return '';
    
    // Look for common date patterns
    const datePatterns = [
      /\b(19|20)\d{2}\b/, // 4-digit years
      /\b\d{1,2}\/\d{1,2}\/(19|20)\d{2}\b/, // MM/DD/YYYY
      /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+(19|20)\d{2}\b/i
    ];

    for (const pattern of datePatterns) {
      const match = ocrText.match(pattern);
      if (match) {
        if (pattern === datePatterns[0]) {
          return match[0]; // Just the year
        } else if (pattern === datePatterns[1]) {
          const parts = match[0].split('/');
          return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        } else {
          // Parse month name format
          const dateParts = match[0].split(/[,\s]+/);
          const year = dateParts[dateParts.length - 1];
          const month = getMonthNumber(dateParts[0]);
          const day = dateParts[1] || '01';
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
      }
    }

    return new Date().getFullYear().toString();
  };

  const getMonthNumber = (monthName: string): string => {
    const months = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    const index = months.findIndex(month => month.startsWith(monthName.toLowerCase()));
    return (index + 1).toString();
  };

  const createFormDataFromMetadata = (): DocumentFormData => {
    if (!uploadedFile || !extractedMetadata) {
      throw new Error('Missing upload data or metadata');
    }

    return {
      title: extractedMetadata.title || 'Untitled Document',
      description: extractedMetadata.description || '',
      document_type: (extractedMetadata.document_type as any) || 'document',
      original_date: extractedMetadata.original_date || new Date().getFullYear().toString(),
      original_date_precision: (extractedMetadata.original_date_precision as any) || 'year',
      location: extractedMetadata.location || '',
      creator: extractedMetadata.creator || '',
      source: extractedMetadata.source || 'Ontario Beach Trading Company Collection',
      archive_source_location: extractedMetadata.archive_source_location || '',
      historical_context: extractedMetadata.historical_context || '',
      visibility: 'public',
      publication_name: extractedMetadata.publication_name || '',
      page_number: extractedMetadata.page_number || '',
      section: extractedMetadata.section || '',
      language: 'en',
      entities: extractedMetadata.extracted_entities?.people || [],
      tags: extractedMetadata.tags || [],
      categories: extractedMetadata.categories || [],
      preservation: {
        source_archive: 'Ontario Beach Trading Company Digital Archive',
        condition: 'good'
      }
    };
  };

  const handleFormSubmit = (data: DocumentFormData) => {
    setFormData(data);
    setCurrentStep('review');
  };

  const handleFinalSubmit = () => {
    if (formData && uploadedFile) {
      onSubmit(formData, uploadedFile);
      setCurrentStep('complete');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Upload Historical Document</h3>
              <p className="text-muted-foreground">
                Select an image file to begin the document creation process. 
                Our AI will automatically extract text and metadata.
              </p>
            </div>
            
            <DocumentFileManager
              documentId={undefined}
              uploadedFile={uploadedFile}
              onFileUpload={handleFileUpload}
              isUploading={isProcessing}
              isLoading={isLoading}
            />
          </div>
        );

      case 'processing':
        return (
          <div className="space-y-6 text-center">
            <Bot className="h-12 w-12 mx-auto text-blue-500 animate-pulse" />
            <div>
              <h3 className="text-lg font-semibold mb-2">Processing with AI</h3>
              <p className="text-muted-foreground mb-4">
                Our AI is analyzing your document and extracting metadata...
              </p>
              <Progress value={processingProgress} className="w-full max-w-md mx-auto" />
              <p className="text-sm text-muted-foreground mt-2">{processingProgress}% complete</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground max-w-md mx-auto">
              <div className="flex items-center gap-2">
                {processingProgress > 20 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                Uploading file
              </div>
              <div className="flex items-center gap-2">
                {processingProgress > 60 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                Extracting text
              </div>
              <div className="flex items-center gap-2">
                {processingProgress > 80 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                Analyzing content
              </div>
              <div className="flex items-center gap-2">
                {processingProgress >= 100 ? <CheckCircle className="h-4 w-4 text-green-500" /> : <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                Generating metadata
              </div>
            </div>
          </div>
        );

      case 'form':
        if (!uploadedFile || !extractedMetadata) {
          return <div>Error: Missing data</div>;
        }

        const preFilledData = createFormDataFromMetadata();

        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">Review Extracted Data</h3>
                <p className="text-muted-foreground">
                  AI has pre-filled the form. Review and edit as needed.
                </p>
              </div>
              
              <div className="flex gap-2">
                {extractedMetadata.tags && extractedMetadata.tags.length > 0 && (
                  <Badge variant="secondary">
                    {extractedMetadata.tags.length} tags extracted
                  </Badge>
                )}
                {extractedMetadata.extracted_entities?.people && extractedMetadata.extracted_entities.people.length > 0 && (
                  <Badge variant="secondary">
                    {extractedMetadata.extracted_entities.people.length} people found
                  </Badge>
                )}
              </div>
            </div>

            <DocumentForm
              initialData={preFilledData}
              onSubmit={handleFormSubmit}
              onCancel={() => setCurrentStep('upload')}
              uploadedFile={uploadedFile}
              isLoading={isLoading}
            />
          </div>
        );

      case 'review':
        if (!formData || !uploadedFile) {
          return <div>Error: Missing form data</div>;
        }

        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold">Final Review</h3>
              <p className="text-muted-foreground">
                Please review the document details before creating.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Document Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="font-medium">Title:</span>
                    <p className="text-sm text-muted-foreground">{formData.title}</p>
                  </div>
                  <div>
                    <span className="font-medium">Type:</span>
                    <p className="text-sm text-muted-foreground">{formData.document_type}</p>
                  </div>
                  <div>
                    <span className="font-medium">Date:</span>
                    <p className="text-sm text-muted-foreground">{formData.original_date}</p>
                  </div>
                  <div>
                    <span className="font-medium">Source:</span>
                    <p className="text-sm text-muted-foreground">{formData.source}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">File Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="font-medium">File:</span>
                    <p className="text-sm text-muted-foreground">{uploadedFile.filename}</p>
                  </div>
                  <div>
                    <span className="font-medium">Size:</span>
                    <p className="text-sm text-muted-foreground">
                      {(uploadedFile.file_size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <div>
                    <span className="font-medium">Type:</span>
                    <p className="text-sm text-muted-foreground">{uploadedFile.file_type}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCurrentStep('form')}>
                Back to Edit
              </Button>
              <Button onClick={handleFinalSubmit} disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Document'}
              </Button>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <CheckCircle className="h-16 w-16 mx-auto text-green-500" />
            <div>
              <h3 className="text-xl font-semibold mb-2">Document Created Successfully!</h3>
              <p className="text-muted-foreground">
                Your historical document has been processed and added to the collection.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Add New Document</h2>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        
        <Progress value={getProgressPercentage()} className="w-full" />
        
        <div className="flex justify-between items-center">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = index < getCurrentStepIndex();
            
            return (
              <div key={step.id} className="flex items-center">
                <div className={`
                  flex items-center justify-center w-8 h-8 rounded-full border-2 
                  ${isActive 
                    ? 'border-blue-500 bg-blue-500 text-white' 
                    : isCompleted 
                      ? 'border-green-500 bg-green-500 text-white' 
                      : 'border-gray-300 text-gray-400'
                  }
                `}>
                  {isCompleted ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </div>
                <span className={`ml-2 text-sm ${isActive || isCompleted ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="pt-6">
          {renderStepContent()}
        </CardContent>
      </Card>
    </div>
  );
}
